import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import MainTitle from "../../../components/headline/MainTitle";
import Translation from "../../../utils/Translation";
import { allLinks } from "../../../utils/helpers";
import ImportProductsModal from "../../../components/modal/ImportProductsModal";
import { tableCellClasses } from "@mui/material/TableCell";
import ApiService from "../../../services/api/api_service";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#005d6d",
    color: theme.palette.common.white,
    fontWeight: "bold",
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'error';
    case 'processing':
      return 'warning';
    case 'pending':
    case 'queued':
      return 'info';
    case 'rolled_back':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusText = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'succeeded':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Pending';
    case 'queued':
      return 'Queued';
    case 'rolled_back':
      return 'Rolled Back';
    default:
      return status || 'Unknown';
  }
};

function ProductImports() {
  const auth = useSelector((state) => state.auth);
  const dynamicState = useSelector((state) => state.dynamicConfig);
  const { sellerInfo } = useSelector((state) => state.seller);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: '',
    importId: null,
    title: '',
    message: '',
  });

  useEffect(() => {
    if (sellerInfo?.id) {
      loadImports();
    }
  }, [page, rowsPerPage, sellerInfo?.id]);

  const loadImports = async () => {
    if (!sellerInfo?.id) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        store_id: sellerInfo.id.toString()
      });

      const headers = {
        'Authorization': `Bearer ${auth.user.accessToken}`,
        'contentType': 'application/json'
      };

      const data = {};
      const options = { reformatData: true };

      const response = await ApiService.get(`/v1/products/imports?${params}`, headers, data, options);

      if (response.data && response.data.payload) {
        setImports(response.data.payload.imports || []);
        setTotalCount(response.data.payload.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Failed to load imports:', error);
      setImports([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenImportModal = () => {
    setOpenImportModal(true);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
    loadImports(); // Refresh the list after import
  };

  const handleAction = (action, importId) => {
    let title, message;
    
    switch (action) {
      case 'delete':
        title = 'Delete Import';
        message = 'Are you sure you want to delete this import? This action cannot be undone.';
        break;
      case 'rollback':
        title = 'Rollback Import';
        message = 'Are you sure you want to rollback this import? All products from this import will be removed.';
        break;
      default:
        return;
    }

    setConfirmDialog({
      open: true,
      action,
      importId,
      title,
      message,
    });
  };

  const handleConfirmAction = async () => {
    const { action, importId } = confirmDialog;
    
    try {
      setLoading(true);
      
      const headers = {
        'Authorization': `Bearer ${auth.user.accessToken}`,
        'contentType': 'application/json'
      };

      const data = {};
      const options = { reformatData: true };

      switch (action) {
        case 'delete':
          const deleteParams = new URLSearchParams({
            store_id: sellerInfo.id.toString()
          });
          await ApiService.delete(`/v1/products/imports/${importId}?${deleteParams}`, headers, data, options);
          break;
        case 'rollback':
          const rollbackParams = new URLSearchParams({
            store_id: sellerInfo.id.toString()
          });
          await ApiService.post(`/v1/products/imports/${importId}/rollback?${rollbackParams}`, headers, data, options);
          break;
      }
      
      await loadImports();
    } catch (error) {
      console.error(`Failed to ${action} import:`, error);
      // You could add a toast notification here for error handling
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: '', importId: null, title: '', message: '' });
    }
  };

  const handleDownloadErrorReport = (url) => {
    // TODO: Implement download functionality
    console.log('Downloading error report:', url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const goBackToStore = () => {
    navigate(`/${allLinks[4]}`); // Navigate back to store
  };

  const handleManageAddresses = () => {
    // Navigate to manage addresses page - update with correct route
    navigate('/address'); // Update this route as needed
  };

  return (
    <Box>
      <MainTitle title="Product Imports" />

      <Container className="no-padding page_size">
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Button
              variant="outlined"
              onClick={goBackToStore}
              sx={{ minWidth: 120 }}
            >
              <Translation data="store_seller.back_to_store" />
            </Button>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={loadImports}
                startIcon={<RefreshIcon />}
                disabled={loading}
              >
                <Translation data="common.refresh" />
              </Button>
              
              <Button
                variant="contained"
                onClick={handleManageAddresses}
                startIcon={<LocationOnIcon />}
              >
                Manage Addresses
              </Button>
              
              <Button
                variant="contained"
                onClick={handleOpenImportModal}
                startIcon={<AddIcon />}
              >
                <Translation data="store.import_products" />
              </Button>
            </Stack>
          </Stack>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>ID</StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store.import.filename" />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="common.status" />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store.import.created_at" />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store.import.completed_at" />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store.import.total_products" />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="common.actions" />
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {imports.map((importItem) => (
                    <StyledTableRow key={importItem.id}>
                      <TableCell>{importItem.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {importItem.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(importItem.status)}
                          color={getStatusColor(importItem.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(importItem.created_at)}</TableCell>
                      <TableCell>{formatDate(importItem.completed_at)}</TableCell>
                      <TableCell>{importItem.total_products}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                         
                          <IconButton
                            size="small"
                            onClick={() => handleAction('delete', importItem.id)}
                            title="Delete Import"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        )}

        <ImportProductsModal
          open={openImportModal}
          onClose={handleCloseImportModal}
          sellerInfo={sellerInfo}
          auth={auth}
          dynamicState={dynamicState}
        />

        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            >
              <Translation data="common.cancel" />
            </Button>
            <Button
              onClick={handleConfirmAction}
              color={confirmDialog.action === 'delete' ? 'error' : 'warning'}
              variant="contained"
            >
              <Translation data={`common.${confirmDialog.action}`} />
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default ProductImports;
